import { IPayWithGatewayTransaction } from '../../../@pano-project/types/transaction';

export enum EPaymentInstrument {
  Card = 'card',
  GooglePay = 'google_pay',
  ApplePay = 'apple_pay',
}

export type TPaymentInstrument = EPaymentInstrument | null;

export type TDigitalWalletProvider = EPaymentInstrument.GooglePay | EPaymentInstrument.ApplePay;

/**
 * PRX-23109 (Revision 2, N1): two coexisting ways a merchant can configure
 * Google Pay / Apple Pay for a Credit Card gateway:
 * - `Separate`: Google Pay / Apple Pay are their own top-level payment
 *   methods (GOOGLE_PAY_CC_KEY / APPLE_PAY_CC_KEY), selected directly from
 *   the payment-method list. This is the only mode the app currently acts
 *   on (2.4.4-2.4.6, T4.x/T5.x).
 * - `Embedded`: one Credit Card method lists which wallet providers it
 *   supports (`card_tokenization_options`), and the customer picks between
 *   Card/Google Pay/Apple Pay via an in-page toggle instead of the
 *   payment-method list -- the pre-PRX-23109 behavior, being restored to
 *   run alongside the newer one (N2/N3/N4).
 */
export enum EWalletConfigMode {
  Separate = 'separate',
  Embedded = 'embedded',
}

export interface IBillingAddress {
  full_name: string;
  country: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  phone: string;
  email: string;
}

export interface IDigitalWalletTokenizedSuccess {
  event: 'onDigitalWalletTokenized';
  tokenized: true;
  token_provider: TDigitalWalletProvider;
  token_data: {
    billing_address: IBillingAddress;
  };
  id: string;
  hpf_auth_token?: string;
}

export interface IDigitalWalletTokenizedFail {
  event: 'onDigitalWalletTokenized';
  tokenized: false;
  token_provider: TDigitalWalletProvider;
  token_data: null;
}

export interface GooglePaySuccess extends IDigitalWalletTokenizedSuccess {
  token_provider: EPaymentInstrument.GooglePay;
  hpf_auth_token: string;
}

export interface ApplePaySuccess extends IDigitalWalletTokenizedSuccess {
  token_provider: EPaymentInstrument.ApplePay;
  hpf_auth_token: string;
}

export type TDigitalWalletTokenizedEvent =
  GooglePaySuccess | ApplePaySuccess | IDigitalWalletTokenizedFail;

export interface IUseDigitalWallets {
  isPossible: boolean;
  isSelected: boolean;
  isLiveWalletSession: boolean;
  isWalletWindowOpen: boolean;
  picInitiated: boolean;
  digitalWalletError: string | null;
  paymentInstrument: TPaymentInstrument;
  digitalWalletTokenizedData: TDigitalWalletTokenizedEvent;
  digitalBillingAddress: IBillingAddress;
  currencyList: ITokenizationProviderOptions['currency_list'];
  cardTokenizationOptions: ICardTokenizationOptions;
  // PRX-23109 (Revision 2, N1): which of the two coexisting configurations
  // (see EWalletConfigMode) the currently selected Credit Card gateway is
  // in, plus the old, in-page toggle's own current choice and its own
  // "a wallet is picked" meaning. These are deliberately separate, newly
  // named fields. `paymentInstrument` above keeps its exact current
  // meaning (mirroring whichever gateway the customer selected from the
  // payment-method list) and must not be repurposed or overloaded by the
  // embedded toggle. `isSelected`, unlike `paymentInstrument`, is
  // deliberately widened (PRX-23109, user-reported) to also mean "a
  // wallet is the active payment instrument" for the embedded
  // configuration -- see useDigitalWallets.ts's own doc comment on it.
  walletConfigMode: EWalletConfigMode;
  embeddedToggleInstrument: TPaymentInstrument;
  isEmbeddedWalletToggleSelected: boolean;
  // PRX-23109 (user-reported): is the embedded configuration genuinely
  // active for the currently selected gateway, with its own toggle
  // actually pointing at a wallet right now -- unlike
  // `isEmbeddedWalletToggleSelected` alone, this also requires the
  // embedded configuration itself to be the one in play (mirrors
  // isEmbeddedWalletConfigActive from wallet-visibility.ts). Consumers
  // that need "is a wallet the active instrument right now, under either
  // configuration" should check `checkIsDigitalWallet(paymentInstrument)
  // || isEmbeddedWalletActive`.
  isEmbeddedWalletActive: boolean;
  // PRX-23109 (T4.33, user-reported): is a saved-card template the
  // customer's actual, already-made selection right now, in the sense that
  // matters for the embedded toggle (see
  // isSavedCardTemplateActiveForEmbeddedWallet's own doc comment in
  // wallet-visibility.ts, and this hook's own computation of it). Exposed
  // for credit-card-form.js's own toggle-visibility gate specifically --
  // see that file's consumption of this field for why it needs this on top
  // of its own, separately-driven `isAddCardMode`.
  isSavedCardTemplateSelected: boolean;
  onToggleEmbeddedWalletOrCard: (value: TPaymentInstrument) => void;
  // PRX-23109 (T4.35, user-reported, regression from T4.33): lets
  // credit-card-form.js mirror its own local `isAddCardMode` (`!isTemplate`)
  // into Redux, so isSavedCardTemplateActiveForEmbeddedWallet
  // (wallet-visibility.ts) can tell "this gateway has a recently-used
  // template available" apart from "the customer is actually, currently
  // choosing to use it right now" -- see that function's own doc comment.
  onAddCardModeChange: (isAddCardModeSelected: boolean) => void;
  onToggleWalletOrCard: (value: TPaymentInstrument) => void;
  onDigitalWalletTokenized: (data: TDigitalWalletTokenizedEvent, onSubmitCallback: any) => void;
  sendTransactionResults: (
    transaction: Partial<IPayWithGatewayTransaction>,
    fallbackMsg: string,
  ) => void;
  resetDigitalWalletError: () => void;
  getProviderName: (name: TPaymentInstrument, viewType?: 'full' | 'short') => string;
  onGetTransactionDetails: (payload: IGetTransactionDetails) => void;
  onDigitalWalletWindowClosed: () => void;
  onTokenizationAvailabilityEvent: (payload: ITokenizationAvailability) => void;
  shakeAmount: (options?: { force?: boolean }) => void;
  onPciInit: (value: boolean) => void;
  isAmountShaking: boolean;
}

export interface ITokenizationProviderOptions {
  token_provider: TDigitalWalletProvider;
  enabled: boolean;
  card_funding_sources: string[];
  card_types: string[];
  currency_list: {
    [key: string]: {
      max_amount: number;
      min_amount: number;
    };
  };
}

// keyed by TDigitalWalletProvider - one entry per digital wallet provider supported by the gateway
export interface ICardTokenizationOptions {
  google_pay: ITokenizationProviderOptions | null;
  apple_pay: ITokenizationProviderOptions | null;
}

export interface ITokenizationAvailability {
  google_pay?: boolean;
  apple_pay?: boolean;
}

export type TPaymentCredentialStatus =
  | 'paymentCredentialsAvailable'
  | 'paymentCredentialsUnavailable'
  | 'paymentCredentialStatusUnknown';

export interface IGetTransactionDetails {
  id: string | number;
  provider?: TDigitalWalletProvider;
  paymentCredentialStatus?: TPaymentCredentialStatus;
}

export interface IUseWalletProvider {
  isEnabled: boolean;
  isSelected: boolean;
  isReadyToPay: boolean;
  isTokenizationChecked: boolean;
  removeBtnFromDOM: boolean;
  // PRX-23109 (T4.30): tri-state result of the 2.4.3 device-compatibility
  // pre-check for this wallet -- `null` when it never ran (no separate
  // tile this session, or a withdrawal), `true`/`false` once confirmed.
  // See checkDigitalWalletDeviceCompatibility.ts's own doc comments.
  isDeviceCompatible: boolean | null;
}
